import { memo, useCallback, useEffect, useState } from "react"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { Range } from "@nova/protobuf"
import { InspectorPanelAccordion } from "../../accordion/InspectorPanelAccordion"
import { InspectorPanelAccordionKeyValueToggle } from "../../accordion/InspectorPanelAccordionKeyValueToggle"
import { activateFlightPath, deactivateFlightPath } from "features/map/layers/slice"
import {
    ColorSwatchCategory,
    hexColorSwatches,
    HotspotPointTypes,
    HotspotStatus,
    OrthoPalettes,
} from "@nova/constants"
import { InspectorPanelAccordionSubsections } from "../../accordion/InspectorPanelAccordionSubsections"
import { InspectorPanelAccordionRadioButton } from "../../accordion/InspectorPanelAccordionRadioButton"
import { config } from "@nova/configuration/browser"
import { ButtonSecondary, ButtonStatus } from "@nova/component-btn"
import * as Icons from "@nova/component-icons"
import { UserPlacedHotspots } from "plugins/InfraredImage/HotspotMenu/UserPlacedHotspots"
import { InspectorPanelKeyValueDropdown } from "../../InspectorPanelKeyValueDropdown"
import { decodeNumberToColor, encodeColorToNumber } from "@nova/serializer/src/client/util"
import { ColorPalette } from "../../icons/TODO-put-in-package/ColorPalette"
import classNames from "classnames"
import { OrthoPalette } from "../../icons/TODO-put-in-package/OrthoPalette"
import { InspectorPanelAccordionSlider } from "../../accordion/InspectorPanelAccordionSlider"
import { selectOrthoOpacityById, setOrthoOpacity } from "plugins/Orthomosaic/OrthomosaicSlice"
import { useOptimisticLocalState } from "../../useOptimisticLocalState"
import {
    debouncedDispatchHotspotUpdateActionForAllMergedFlights,
    dispatchHotspotActionForAllMergedFlights,
    dispatchHotspotUpdateActionForAllMergedFlights,
    toggleMergedFlightVisibility,
} from "./module"
import {
    selectAllMergedFilteredHotspots,
    selectAllMergedHotspots,
    selectLargestIndividualFlightInMergedFlightWithScanArea,
    selectMergedFlightPathIsShowing,
    selectMergedOrthoOverlapIsShowing,
    selectMergeLabelsAreVisible,
    selectMergeOrthosAreVisible,
    selectMergePointLayerIsVisible,
    selectMergePolygonsAreVisible,
    selectMergeRemovedHotspotsAreVisible,
    selectMergeScanAreaIsShowing,
    selectSomeFlightHasScanAreaExtraFeature,
} from "./selectors"
import { useInspectorPanelMergedFlight } from "./useInspetorPanelMergedFlights"
import { InspectorPanelMergedFlightLayerFilters } from "./InspectorPanelMergedFlightLayerFilters"
import { scanAreaFillColorSwatches, scanAreaOutlineColorSwatches } from "../../constants"
import { openHotspotMultiRemoval } from "features/data-management/hotspots/slice"
import {
    selectInfraredFlightPointColorById,
    selectInfraredFlightPolyStrokeById,
    selectInfraredFlightPolyFillById,
    selectInfraredFlightOrthoGradientById,
    selectInfraredFlightScanAreaFillColorById,
    selectInfraredFlightScanAreaOutlineColorById,
} from "features/entity-caching/flights/selectors"
import {
    deactivateHotspotOrthoOverlapLayerAction,
    activateHotspotOrthoOverlapLayerAction,
    deactivateHotspotScanAreaLayerAction,
    activateHotspotScanAreaLayerAction,
    deactivateHotspotPointLayerAction,
    activateHotspotPointLayerAction,
    deactivateHotspotLabelsLayerAction,
    activateHotspotLabelsLayerAction,
    deactivateRemovedHotspotsLayerAction,
    activateRemovedHotspotsLayerAction,
    activateHotspotPolygonLayerAction,
    deactivateHotspotPolygonLayerAction,
    deactivateHotspotPolygonAreaLayerAction,
    deactivateHotspotOrthoLayerAction,
    activateHotspotOrthoLayerAction,
} from "features/entity-caching/flights/slice"
import { selectMergedFlightById } from "features/entity-caching/mergedFlights/selectors"
import { batchUpdateMergedFlight } from "features/pubsub/entityActions/cubes/mergedFlights"
import { updateInfraredFlight } from "features/pubsub/entityActions/flights/infrared"

const pointColorSwatches = hexColorSwatches[ColorSwatchCategory.Hotspot].map((hex) =>
    encodeColorToNumber(hex)
)

const polygonOutlineColorSwatches = hexColorSwatches[ColorSwatchCategory.PolygonOutline].map(
    (hex) => encodeColorToNumber(hex)
)
const polygonFillColorSwatches = hexColorSwatches[ColorSwatchCategory.PolygonFill].map((hex) =>
    encodeColorToNumber(hex)
)

const InspectorPanelMergedFlightLayer = memo(({ id }: { id: string }) => {
    const dispatch = useAppDispatch()

    const flight = useAppSelector(selectMergedFlightById(id))

    const {
        flightPathDistance,
        formattedOrthoArea,
        formattedPolygonsArea,
        appearanceReferenceFlight,
        mergedFlight,
        hotspotFlightIds = [],
    } = useInspectorPanelMergedFlight(flight?.id ?? "")

    const allHotspots = useAppSelector(selectAllMergedHotspots(hotspotFlightIds))
    const filteredHotspots = useAppSelector(
        selectAllMergedFilteredHotspots(hotspotFlightIds)
    ).filter((hotspot) => hotspot.status !== HotspotStatus.Removed)

    const [openCategories, setOpenCategories] = useState(new Set<string>())

    const flightPathIsEnabled = useAppSelector(selectMergedFlightPathIsShowing(hotspotFlightIds))
    const orthoOverlapIsEnabled = useAppSelector(
        selectMergedOrthoOverlapIsShowing(hotspotFlightIds)
    )
    const scanAreaIsEnabled = useAppSelector(selectMergeScanAreaIsShowing(hotspotFlightIds))
    const pointsIsEnabled = useAppSelector(selectMergePointLayerIsVisible(hotspotFlightIds))
    const selectedPointAppearance = appearanceReferenceFlight?.pointType || HotspotPointTypes.Point
    const polygonsAreEnabled = useAppSelector(selectMergePolygonsAreVisible(hotspotFlightIds))
    const extraFeaturesJSON = appearanceReferenceFlight?.job?.extraFeatures
    const { useLabelLayer: labelsAreAllowed } = config.use("useLabelLayer")
    const labelsAreEnabled = useAppSelector(selectMergeLabelsAreVisible(hotspotFlightIds))
    const [isPlacingHotspot, setIsPlacingHotspot] = useState(false)
    const orthomosaicIsEnabled = useAppSelector(selectMergeOrthosAreVisible(hotspotFlightIds))
    const existingOpacity = useAppSelector(selectOrthoOpacityById(appearanceReferenceFlight!.id))
    const someFlightHasScanAreaExtraFeature = useAppSelector(
        selectSomeFlightHasScanAreaExtraFeature(flight.id)
    )
    const largestIndividualFlightWithScanArea = useAppSelector(
        selectLargestIndividualFlightInMergedFlightWithScanArea(flight.id)
    )
    const removedHotspotsEnabled = useAppSelector(
        selectMergeRemovedHotspotsAreVisible(hotspotFlightIds)
    )

    // This does NOT refer to categories, but rather individual
    // dropdowns that exist inside of any category.
    const [openDropdownId, setOpenDropdownId] = useState<string>()

    const setCategoryIsOpen = useCallback((category: string, isOpen: boolean) => {
        setOpenCategories((prev) => {
            const openCategories = new Set(prev)
            if (isOpen) openCategories.add(category)
            else openCategories.delete(category)
            return openCategories
        })
    }, [])

    const setHotspotPointType = useCallback(
        (pointType: HotspotPointTypes) => {
            dispatch(
                batchUpdateMergedFlight({
                    id: flight.id,
                    updates: {
                        pointType,
                    },
                })
            )
        },
        [dispatch, flight.id]
    )

    const [resizeTrigger, triggerResize] = useState(0)
    useEffect(() => {
        setTimeout(() => triggerResize((prev) => ++prev), 0)
    }, [openDropdownId, appearanceReferenceFlight?.pointType])

    const [pointColor, setPointColor] = useOptimisticLocalState<number>(
        selectInfraredFlightPointColorById(appearanceReferenceFlight!.id),
        (pointColor) => {
            if (!flight) return

            const existingRange =
                appearanceReferenceFlight?.perimeterDistance?.range &&
                (
                    JSON.parse(
                        JSON.stringify(appearanceReferenceFlight?.perimeterDistance.range)
                    ) as Range[]
                ).sort((a, b) => a.distance - b.distance)
            if (existingRange?.at(0)) existingRange[0].color = pointColor

            dispatchHotspotUpdateActionForAllMergedFlights(updateInfraredFlight, hotspotFlightIds, {
                pointColor,
                perimeterDistance: appearanceReferenceFlight?.perimeterDistance && {
                    ...appearanceReferenceFlight.perimeterDistance,
                    range: existingRange || [],
                },
            })
        },
        appearanceReferenceFlight?.pointColor || pointColorSwatches[0]
    )

    const [polygonOutline, setPolygonOutline] = useOptimisticLocalState<number>(
        selectInfraredFlightPolyStrokeById(appearanceReferenceFlight!.id),
        (polyStroke) => {
            dispatchHotspotUpdateActionForAllMergedFlights(updateInfraredFlight, hotspotFlightIds, {
                polyStroke,
            })
        },
        appearanceReferenceFlight?.polyStroke || polygonOutlineColorSwatches[0]
    )

    const [polygonFill, setPolygonFill] = useOptimisticLocalState<number>(
        selectInfraredFlightPolyFillById(appearanceReferenceFlight!.id),
        (polyFill) => {
            dispatchHotspotUpdateActionForAllMergedFlights(updateInfraredFlight, hotspotFlightIds, {
                polyFill,
            })
        },
        appearanceReferenceFlight?.polyFill || polygonFillColorSwatches[0]
    )

    const [palette, setPalette] = useOptimisticLocalState<OrthoPalettes>(
        selectInfraredFlightOrthoGradientById(appearanceReferenceFlight!.id),
        (orthoGradient) => {
            dispatch(
                batchUpdateMergedFlight({
                    id: flight.id,
                    updates: {
                        orthoGradient,
                    },
                })
            )
        },
        appearanceReferenceFlight?.orthoGradient || OrthoPalettes.WhiteHot
    )

    // These optimistic local states are only used if all flights in the merged flight have the same styling
    const [scanAreaFillColor, setScanAreaFillColor] = useOptimisticLocalState<number>(
        selectInfraredFlightScanAreaFillColorById(largestIndividualFlightWithScanArea?.id || ""),
        (scanAreaFillColor) => {
            if (!flight) return

            dispatch(
                batchUpdateMergedFlight({
                    id: flight.id,
                    updates: {
                        scanAreaFillColor,
                    },
                })
            )
        },
        largestIndividualFlightWithScanArea?.scanAreaFillColor || scanAreaFillColorSwatches[0]
    )
    const [scanAreaOutlineColor, setScanAreaOutlineColor] = useOptimisticLocalState<number>(
        selectInfraredFlightScanAreaOutlineColorById(largestIndividualFlightWithScanArea?.id || ""),
        (scanAreaOutlineColor) => {
            if (!flight) return

            dispatch(
                batchUpdateMergedFlight({
                    id: flight.id,
                    updates: {
                        scanAreaOutlineColor,
                    },
                })
            )
        },
        largestIndividualFlightWithScanArea?.scanAreaOutlineColor || scanAreaOutlineColorSwatches[0]
    )

    if (!appearanceReferenceFlight || !mergedFlight) return null

    return (
        <div className="flex flex-col gap-2 mb-2">
            {/* Flight Settings */}
            <InspectorPanelAccordion
                title="Flight"
                subtitle={flightPathDistance}
                isOpen={openCategories.has("flight")}
                setIsOpen={(isOpen) => setCategoryIsOpen("flight", isOpen)}
                isToggled={flightPathIsEnabled && flight.visible}
                setIsToggled={() => {
                    if (flightPathIsEnabled && flight.visible) {
                        dispatchHotspotActionForAllMergedFlights(
                            deactivateFlightPath,
                            mergedFlight.hotspotFlightIds
                        )
                    } else {
                        dispatchHotspotActionForAllMergedFlights(
                            activateFlightPath,
                            mergedFlight.hotspotFlightIds
                        )
                    }

                    if (!flight.visible)
                        dispatch(toggleMergedFlightVisibility({ entity: mergedFlight }))
                }}
            >
                {orthoOverlapIsEnabled && (
                    <InspectorPanelAccordionKeyValueToggle
                        keyTitle="Overlap"
                        isToggled={orthoOverlapIsEnabled && flight.visible}
                        setIsToggled={() => {
                            if (orthoOverlapIsEnabled && flight.visible) {
                                dispatchHotspotUpdateActionForAllMergedFlights(
                                    deactivateHotspotOrthoOverlapLayerAction,
                                    mergedFlight.hotspotFlightIds
                                )
                            } else {
                                dispatchHotspotUpdateActionForAllMergedFlights(
                                    activateHotspotOrthoOverlapLayerAction,
                                    mergedFlight.hotspotFlightIds
                                )
                            }

                            if (!flight.visible)
                                dispatch(toggleMergedFlightVisibility({ entity: mergedFlight }))
                        }}
                    />
                )}

                {someFlightHasScanAreaExtraFeature && (
                    <>
                        <InspectorPanelAccordionKeyValueToggle
                            keyTitle="Scan Area"
                            isToggled={scanAreaIsEnabled && flight.visible}
                            setIsToggled={() => {
                                if (scanAreaIsEnabled && flight.visible) {
                                    dispatchHotspotUpdateActionForAllMergedFlights(
                                        deactivateHotspotScanAreaLayerAction,
                                        mergedFlight.hotspotFlightIds
                                    )
                                } else {
                                    dispatchHotspotUpdateActionForAllMergedFlights(
                                        activateHotspotScanAreaLayerAction,
                                        mergedFlight.hotspotFlightIds
                                    )
                                }

                                if (!flight.visible)
                                    dispatch(toggleMergedFlightVisibility({ entity: mergedFlight }))
                            }}
                        />
                        <InspectorPanelKeyValueDropdown
                            id="scanarea-outline-color"
                            isOpen={openDropdownId === "scanarea-outline-color"}
                            setOpenDropdownId={setOpenDropdownId}
                            keyTitle="Outline"
                            Icon={Icons.BucketColor}
                            iconClasses={classNames({
                                "text-gray-0": scanAreaIsEnabled && flight.visible,
                                "text-gray-500": !(scanAreaIsEnabled && flight.visible),
                            })}
                            iconAccentColor={
                                scanAreaIsEnabled &&
                                flight.visible &&
                                largestIndividualFlightWithScanArea?.scanAreaOutlineColor
                                    ? decodeNumberToColor(
                                          largestIndividualFlightWithScanArea?.scanAreaOutlineColor
                                      )
                                    : "#000"
                            }
                            disabled={!(scanAreaIsEnabled && flight.visible)}
                            className="ml-6"
                            includeSpacer
                            isInSubSection
                            align="right"
                        >
                            <ColorPalette
                                swatches={scanAreaOutlineColorSwatches}
                                selectedColor={scanAreaOutlineColor}
                                setSelectedColor={setScanAreaOutlineColor}
                                setOpenDropdownId={setOpenDropdownId}
                                triggerResize={() => triggerResize((prev) => ++prev)}
                            />
                        </InspectorPanelKeyValueDropdown>

                        <InspectorPanelKeyValueDropdown
                            id="scanarea-fill-color"
                            isOpen={openDropdownId === "scanarea-fill-color"}
                            setOpenDropdownId={setOpenDropdownId}
                            keyTitle="Fill"
                            Icon={Icons.BucketColor}
                            iconClasses={classNames({
                                "text-gray-0": scanAreaIsEnabled && flight.visible,
                                "text-gray-500": !(scanAreaIsEnabled && flight.visible),
                            })}
                            iconAccentColor={
                                scanAreaIsEnabled &&
                                flight.visible &&
                                largestIndividualFlightWithScanArea?.scanAreaFillColor
                                    ? decodeNumberToColor(
                                          largestIndividualFlightWithScanArea?.scanAreaFillColor
                                      )
                                    : "#000"
                            }
                            disabled={!(scanAreaIsEnabled && flight.visible)}
                            className="ml-6"
                            includeSpacer
                            isInSubSection
                            align="right"
                        >
                            <ColorPalette
                                swatches={scanAreaFillColorSwatches}
                                selectedColor={scanAreaFillColor}
                                setSelectedColor={setScanAreaFillColor}
                                setOpenDropdownId={setOpenDropdownId}
                                triggerResize={() => triggerResize((prev) => ++prev)}
                            />
                        </InspectorPanelKeyValueDropdown>
                    </>
                )}
            </InspectorPanelAccordion>
            {allHotspots.length > 0 && (
                <>
                    <InspectorPanelMergedFlightLayerFilters
                        id={id}
                        openCategories={openCategories}
                        setCategoryIsOpen={setCategoryIsOpen}
                    />

                    <InspectorPanelAccordion
                        title="Hotspots"
                        subtitle={`${filteredHotspots.length} hotspot${filteredHotspots.length !== 1 ? "s" : ""}`}
                        isToggled={pointsIsEnabled && flight.visible}
                        setIsToggled={() => {
                            if (pointsIsEnabled && flight.visible) {
                                dispatchHotspotUpdateActionForAllMergedFlights(
                                    deactivateHotspotPointLayerAction,
                                    mergedFlight.hotspotFlightIds
                                )
                            } else {
                                dispatchHotspotUpdateActionForAllMergedFlights(
                                    activateHotspotPointLayerAction,
                                    mergedFlight.hotspotFlightIds
                                )
                            }

                            setCategoryIsOpen("points", !pointsIsEnabled)

                            if (!flight.visible)
                                dispatch(toggleMergedFlightVisibility({ entity: mergedFlight }))
                        }}
                        isOpen={openCategories.has("points")}
                        setIsOpen={(isOpen) => setCategoryIsOpen("points", isOpen)}
                        hasSubsections={true}
                        shouldPadChildrenVertically={false}
                        extraVerticalPaddingInRem={0}
                        extraResizeDependency={resizeTrigger}
                    >
                        <InspectorPanelAccordionSubsections>
                            {/* Hotspot Appearance */}
                            <div>
                                <h5 className="text-gray-0 mb-1 select-none">Appearance</h5>
                                {/* Point */}
                                <InspectorPanelAccordionRadioButton
                                    title="Point"
                                    isSelected={selectedPointAppearance === HotspotPointTypes.Point}
                                    setIsSelected={() =>
                                        setHotspotPointType(HotspotPointTypes.Point)
                                    }
                                >
                                    {/* Relative Size */}
                                    {!!extraFeaturesJSON?.includes("hotspotArea") && (
                                        <InspectorPanelAccordionKeyValueToggle
                                            keyTitle="Relative Size"
                                            isToggled={
                                                selectedPointAppearance ===
                                                    HotspotPointTypes.Point &&
                                                !!appearanceReferenceFlight?.relativeSizeEnabled
                                            }
                                            setIsToggled={() =>
                                                dispatch(
                                                    batchUpdateMergedFlight({
                                                        id: flight.id,
                                                        updates: {
                                                            relativeSizeEnabled:
                                                                !appearanceReferenceFlight?.relativeSizeEnabled,
                                                        },
                                                    })
                                                )
                                            }
                                            isInSubSection={true}
                                            disabled={
                                                selectedPointAppearance !== HotspotPointTypes.Point
                                            }
                                        />
                                    )}

                                    {/* Point Color */}
                                    <InspectorPanelKeyValueDropdown
                                        id="point-color"
                                        isOpen={
                                            openDropdownId === "point-color" &&
                                            selectedPointAppearance === HotspotPointTypes.Point
                                        }
                                        setOpenDropdownId={setOpenDropdownId}
                                        keyTitle="Color"
                                        Icon={Icons.BucketColor}
                                        iconClasses={classNames({
                                            "text-gray-0":
                                                selectedPointAppearance === HotspotPointTypes.Point,
                                            "text-gray-500":
                                                selectedPointAppearance !== HotspotPointTypes.Point,
                                        })}
                                        iconAccentColor={
                                            selectedPointAppearance === HotspotPointTypes.Point
                                                ? decodeNumberToColor(pointColor)
                                                : "#404044" // gray-500
                                        }
                                        isInSubSection={true}
                                        disabled={
                                            selectedPointAppearance !== HotspotPointTypes.Point
                                        }
                                        align="right"
                                    >
                                        <ColorPalette
                                            swatches={pointColorSwatches}
                                            selectedColor={pointColor}
                                            setSelectedColor={setPointColor}
                                            setOpenDropdownId={setOpenDropdownId}
                                            triggerResize={() => triggerResize((prev) => ++prev)}
                                        />
                                    </InspectorPanelKeyValueDropdown>
                                </InspectorPanelAccordionRadioButton>

                                {/* Heat */}
                                <InspectorPanelAccordionRadioButton
                                    title="Heat"
                                    isSelected={
                                        appearanceReferenceFlight?.pointType ===
                                        HotspotPointTypes.Heat
                                    }
                                    setIsSelected={() =>
                                        setHotspotPointType(HotspotPointTypes.Heat)
                                    }
                                />

                                {/* Hex */}
                                <InspectorPanelAccordionRadioButton
                                    title="Hex"
                                    isSelected={
                                        appearanceReferenceFlight?.pointType ===
                                        HotspotPointTypes.Hex
                                    }
                                    setIsSelected={() => setHotspotPointType(HotspotPointTypes.Hex)}
                                >
                                    {/* Flatten */}
                                    {!!extraFeaturesJSON?.includes("hotspotArea") &&
                                        !!extraFeaturesJSON?.includes("hotspotThermal") && (
                                            <InspectorPanelAccordionKeyValueToggle
                                                keyTitle="Flatten"
                                                isToggled={
                                                    selectedPointAppearance ===
                                                        HotspotPointTypes.Hex &&
                                                    !!appearanceReferenceFlight.hexIsFlat
                                                }
                                                setIsToggled={() =>
                                                    dispatch(
                                                        batchUpdateMergedFlight({
                                                            id: flight.id,
                                                            updates: {
                                                                hexIsFlat:
                                                                    !appearanceReferenceFlight.hexIsFlat,
                                                            },
                                                        })
                                                    )
                                                }
                                                isInSubSection
                                                disabled={
                                                    selectedPointAppearance !==
                                                    HotspotPointTypes.Hex
                                                }
                                            />
                                        )}
                                </InspectorPanelAccordionRadioButton>
                            </div>

                            <div>
                                {/* Hotspot Labels */}
                                {labelsAreAllowed && (
                                    <InspectorPanelAccordionKeyValueToggle
                                        keyTitle="Show Labels"
                                        isToggled={labelsAreEnabled}
                                        setIsToggled={() =>
                                            labelsAreEnabled
                                                ? dispatchHotspotUpdateActionForAllMergedFlights(
                                                      deactivateHotspotLabelsLayerAction,
                                                      hotspotFlightIds
                                                  )
                                                : dispatchHotspotUpdateActionForAllMergedFlights(
                                                      activateHotspotLabelsLayerAction,
                                                      hotspotFlightIds
                                                  )
                                        }
                                    />
                                )}

                                <InspectorPanelAccordionKeyValueToggle
                                    keyTitle="Show Removed Hotspots"
                                    isToggled={removedHotspotsEnabled}
                                    setIsToggled={() => {
                                        if (removedHotspotsEnabled) {
                                            dispatchHotspotUpdateActionForAllMergedFlights(
                                                deactivateRemovedHotspotsLayerAction,
                                                hotspotFlightIds
                                            )
                                        } else {
                                            dispatchHotspotUpdateActionForAllMergedFlights(
                                                activateRemovedHotspotsLayerAction,
                                                hotspotFlightIds
                                            )
                                        }
                                    }}
                                />
                            </div>

                            {/* Place Manual Hotspot */}
                            <div className="flex flex-col gap-2">
                                <ButtonSecondary
                                    LeftIcon={Icons.Loupe}
                                    title="Place Manual Hotspot"
                                    className="w-full"
                                    status={
                                        isPlacingHotspot
                                            ? ButtonStatus.Selected
                                            : ButtonStatus.Normal
                                    }
                                    onClick={() => setIsPlacingHotspot(!isPlacingHotspot)}
                                />
                                {isPlacingHotspot && (
                                    <UserPlacedHotspots
                                        flightId={appearanceReferenceFlight!.id}
                                        cancelHotspotPlace={() => setIsPlacingHotspot(false)}
                                    />
                                )}
                                <ButtonSecondary
                                    LeftIcon={Icons.RemoveSelection}
                                    title="Remove Hotspots"
                                    className="w-full"
                                    onClick={() =>
                                        dispatch(
                                            openHotspotMultiRemoval({
                                                infraredFlightIds: appearanceReferenceFlight!.id,
                                            })
                                        )
                                    }
                                />
                            </div>
                        </InspectorPanelAccordionSubsections>
                    </InspectorPanelAccordion>
                </>
            )}

            {/* Polygons */}
            {!!extraFeaturesJSON?.includes("newpoly") && allHotspots.length > 0 && (
                <InspectorPanelAccordion
                    title="Hot Areas"
                    subtitle={formattedPolygonsArea && `${formattedPolygonsArea} total area`}
                    isToggled={polygonsAreEnabled && flight.visible}
                    setIsToggled={() => {
                        if (!polygonsAreEnabled || !flight.visible) {
                            dispatchHotspotUpdateActionForAllMergedFlights(
                                activateHotspotPolygonLayerAction,
                                hotspotFlightIds
                            )
                        } else {
                            dispatchHotspotUpdateActionForAllMergedFlights(
                                deactivateHotspotPolygonLayerAction,
                                hotspotFlightIds
                            )
                            dispatchHotspotUpdateActionForAllMergedFlights(
                                deactivateHotspotPolygonAreaLayerAction,
                                hotspotFlightIds
                            )
                        }

                        setCategoryIsOpen("polygons", !polygonsAreEnabled)

                        if (!flight.visible)
                            dispatch(toggleMergedFlightVisibility({ entity: mergedFlight }))
                    }}
                    isOpen={openCategories.has("polygons")}
                    setIsOpen={(isOpen) => setCategoryIsOpen("polygons", isOpen)}
                >
                    <InspectorPanelKeyValueDropdown
                        id="poly-outline"
                        isOpen={openDropdownId === "poly-outline"}
                        setOpenDropdownId={setOpenDropdownId}
                        keyTitle="Outline"
                        Icon={Icons.BucketColor}
                        iconClasses="text-gray-0"
                        iconAccentColor={decodeNumberToColor(polygonOutline)}
                    >
                        <ColorPalette
                            swatches={polygonOutlineColorSwatches}
                            selectedColor={polygonOutline}
                            setSelectedColor={setPolygonOutline}
                            setOpenDropdownId={setOpenDropdownId}
                            triggerResize={() => triggerResize((prev) => ++prev)}
                        />
                    </InspectorPanelKeyValueDropdown>

                    <InspectorPanelKeyValueDropdown
                        id="poly-fill"
                        isOpen={openDropdownId === "poly-fill"}
                        setOpenDropdownId={setOpenDropdownId}
                        keyTitle="Fill"
                        Icon={Icons.BucketColor}
                        iconClasses="text-gray-0"
                        iconAccentColor={decodeNumberToColor(polygonFill)}
                    >
                        <ColorPalette
                            swatches={polygonFillColorSwatches}
                            selectedColor={polygonFill}
                            setSelectedColor={setPolygonFill}
                            setOpenDropdownId={setOpenDropdownId}
                            triggerResize={() => triggerResize((prev) => ++prev)}
                        />
                    </InspectorPanelKeyValueDropdown>
                </InspectorPanelAccordion>
            )}

            {/* Orthomosaic */}
            {!!extraFeaturesJSON?.includes("ortho") && (
                <InspectorPanelAccordion
                    title="IR Orthomosaic"
                    subtitle={formattedOrthoArea && `${formattedOrthoArea} coverage`}
                    isToggled={orthomosaicIsEnabled && flight.visible}
                    setIsToggled={() => {
                        if (orthomosaicIsEnabled && flight.visible) {
                            dispatchHotspotUpdateActionForAllMergedFlights(
                                deactivateHotspotOrthoLayerAction,
                                hotspotFlightIds
                            )
                        } else {
                            dispatchHotspotUpdateActionForAllMergedFlights(
                                activateHotspotOrthoLayerAction,
                                hotspotFlightIds
                            )
                        }

                        setCategoryIsOpen("ortho", !orthomosaicIsEnabled)

                        if (!flight.visible)
                            dispatch(toggleMergedFlightVisibility({ entity: mergedFlight }))
                    }}
                    isOpen={openCategories.has("ortho")}
                    setIsOpen={(isOpen) => setCategoryIsOpen("ortho", isOpen)}
                >
                    <InspectorPanelKeyValueDropdown
                        id="palette"
                        isOpen={openDropdownId === "palette"}
                        setOpenDropdownId={setOpenDropdownId}
                        keyTitle="Palette"
                        Icon={Icons.Gradient}
                        iconClasses="text-gray-0"
                    >
                        <OrthoPalette
                            selectedPalette={palette}
                            setSelectedPalette={setPalette}
                            setOpenDropdownId={setOpenDropdownId}
                        />
                    </InspectorPanelKeyValueDropdown>

                    <InspectorPanelAccordionSlider
                        title="Opacity"
                        resetValue={100}
                        existingSliderValue={existingOpacity}
                        minSliderValue={1}
                        maxSliderValue={100}
                        onValueChanged={(opacity) =>
                            debouncedDispatchHotspotUpdateActionForAllMergedFlights(
                                setOrthoOpacity,
                                hotspotFlightIds,
                                { opacity }
                            )
                        }
                    />
                </InspectorPanelAccordion>
            )}
        </div>
    )
})
InspectorPanelMergedFlightLayer.displayName = "InspectorPanelMergedFlightLayer"
export { InspectorPanelMergedFlightLayer as InspectorPanelMergedFlightLayer }
